<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="vendor-creation-dialog-overlay vendor-creation-dialog-overlay--glass"
      @click="handleOverlayClick"
    >
    <div
      class="vendor-creation-dialog vendor-creation-dialog--glass"
      title="Drag the bottom-right corner to resize the dialog"
      @click.stop
    >
      <div class="vendor-creation-dialog-header vendor-creation-dialog-header--glass">
        <span>Create New Vendor</span>
        <div class="vendor-creation-dialog-icons">
          <i class="bi bi-x-circle" @click="handleClose"></i>
        </div>
      </div>

      <div class="vendor-creation-dialog-content vendor-creation-dialog-content--glass">
        <!-- Tab Navigation -->
        <TabNavigation
          v-model="activeTab"
          :tabs="tabs"
          @update:modelValue="handleTabChange"
        />

        <!-- Tab Content -->
        <div class="vendor-creation-modal-tab-content vendor-creation-modal-tab-content--glass">
        <form class="vendor-creation-tab-form" @submit.prevent="handleNext">
          <!-- General Tab -->
          <div
            class="vendor-creation-tab-panel"
            :class="{ 'vendor-creation-tab-panel--active': activeTab === 'general' }"
            :aria-hidden="activeTab !== 'general'"
          >
          <div class="vendor-creation-form-section vendor-creation-form-section--glass">
            <!-- Row 1: Vendor ID, Vendor Name -->
            <div class="vendor-creation-form-group-row">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                <LabelInputField
                  v-model="form.vendorId"
                  label="Vendor ID"
                  input-id="vendor-id"
                  type="text"
                  required
                  :flex="true"
                  :has-error="vendorIdExists || !!validationErrors.vendorId"
                  @input="handleVendorIdInput"
                >
                  <div v-if="checkingVendorId" class="vendor-creation-checking">Checking availability...</div>
                  <div v-if="vendorIdExists" class="vendor-creation-error-text">This Vendor ID already exists in this company.</div>
                  <div v-if="validationErrors.vendorId" class="vendor-creation-error-text">
                    {{ validationErrors.vendorId }}
                  </div>
                </LabelInputField>
              </div>
              <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                <LabelInputField
                  v-model="form.vendorName"
                  label="Vendor Name"
                  input-id="vendor-name"
                  type="text"
                  required
                  :flex="true"
                  :has-error="!!validationErrors.vendorName"
                  @input="clearFieldError('vendorName')"
                >
                  <div v-if="validationErrors.vendorName" class="vendor-creation-error-text">
                    {{ validationErrors.vendorName }}
                  </div>
                </LabelInputField>
              </div>
            </div>

            <!-- Row 2: Active, Vendor Type -->
            <div class="vendor-creation-form-group-row vendor-creation-form-group-row--field-pair">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-checkbox">
                <LabelCheckboxField
                  input-id="vendor-active"
                  label="Active"
                  v-model="form.active"
                  flex
                />
              </div>
              <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                <LabelDropdown
                  v-model="form.vendorRole"
                  label="Vendor Type"
                  select-id="vendor-role"
                  :flex="true"
                  :searchable="true"
                  :options="vendorRoleDropdownOptions"
                />
              </div>
            </div>

              <!-- Row 2: Vendor Since, Opening Balance -->
            <div class="vendor-creation-form-group-row">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-date">
                  <LabelDateField
                    v-model="form.vendorSince"
                    label="Vendor Since"
                    input-id="vendor-since"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.openingBalance"
                    label="Opening Balance (USD)"
                    input-id="opening-balance"
                    type="number"
                    :flex="true"
                  />
                </div>
              </div>

              <!-- Row 3: Balance as of, Balance -->
              <div class="vendor-creation-form-group-row">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-date">
                  <LabelDateField
                    v-model="form.balanceAsOf"
                    label="Balance as of"
                    input-id="balance-as-of"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.balance"
                    label="Balance (USD)"
                    input-id="balance"
                    type="number"
                    :flex="true"
                    readonly
                  />
                </div>
              </div>

              <!-- Row 4: Fax, Web page address -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.fax"
                    label="Fax"
                    input-id="fax"
                    type="text"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.webpage"
                    label="Web page address"
                    input-id="webpage"
                    type="text"
                    :flex="true"
                  />
              </div>
            </div>

              <!-- Row 5: Tax ID -->
            <div class="vendor-creation-form-group-row">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                <LabelInputField
                  v-model="form.taxId"
                  label="Tax ID"
                  input-id="tax-id"
                  type="text"
                  :flex="true"
                />
              </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="vendor-creation-form-section vendor-creation-form-section--glass">
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-full-width vendor-creation-form-group--label-description">
                  <LabelDescriptionField
                    v-model="form.notes"
                    label="Notes"
                    input-id="vendor-notes"
                    :rows="2"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Details Tab -->
          <div
            class="vendor-creation-tab-panel"
            :class="{ 'vendor-creation-tab-panel--active': activeTab === 'details' }"
            :aria-hidden="activeTab !== 'details'"
          >
          <div class="vendor-creation-form-section vendor-creation-form-section--glass">
              <!-- Row 1: Payment Terms, Payment Method -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.paymentTermId"
                    label="Payment Terms"
                    select-id="payment-terms"
                    required
                    :flex="true"
                    :has-error="!!validationErrors.paymentTermId"
                    :options="[
                      { label: 'Select', value: '' },
                      ...paymentTermsList.map((term) => ({
                        label: `${term.name} (${term.description})`,
                        value: term.id,
                      })),
                      { label: 'Add New Payment Term', value: '__add_new__' },
                    ]"
                    @change="handlePaymentTermChange"
                  >
                    <div v-if="validationErrors.paymentTermId" class="vendor-creation-error-text">
                      {{ validationErrors.paymentTermId }}
                    </div>
                  </LabelDropdown>
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.paymentMethod"
                    label="Payment Method"
                    select-id="payment-method"
                    required
                    :flex="true"
                    :has-error="!!validationErrors.paymentMethod"
                    :options="[
                      { label: 'Select', value: '' },
                      { label: 'Cash', value: 'Cash' },
                      { label: 'Cheque', value: 'Cheque' },
                      { label: 'Credit Card', value: 'Credit Card' },
                      { label: 'Direct Deposit', value: 'Direct Deposit' },
                      { label: 'Master card', value: 'Master card' },
                      { label: 'Online Banking', value: 'Online Banking' },
                      { label: 'Standing Order', value: 'Standing Order' },
                      { label: 'Switch/Maestro', value: 'Switch/Maestro' },
                    ]"
                    @change="clearFieldError('paymentMethod')"
                  >
                    <div v-if="validationErrors.paymentMethod" class="vendor-creation-error-text">
                      {{ validationErrors.paymentMethod }}
                    </div>
                  </LabelDropdown>
                </div>
              </div>

              <!-- Row 2: Bank Name, Bank Account Type -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.bankName"
                    label="Bank Name"
                    input-id="bank-name"
                    type="text"
                    placeholder="Bank Name"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.bankAccountType"
                    label="Bank Account Type"
                    select-id="bank-account-type"
                    :flex="true"
                    :options="[
                      { label: 'Select', value: '' },
                      { label: 'Checking', value: 'Checking' },
                      { label: 'Savings', value: 'Savings' },
                    ]"
                  />
                </div>
              </div>

              <!-- Row 3: Bank Routing Number, Account Number -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.bankRoutingNumber"
                    label="Bank Routing Number"
                    input-id="bank-routing-number"
                    type="text"
                    placeholder="Bank Routing Number"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.accountNumber"
                    label="Account Number"
                    input-id="account-number"
                    type="text"
                    placeholder="Account Number"
                    :flex="true"
                  />
                </div>
              </div>

              <!-- Row 4: Bank Branch, Credit Limit -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.bankBranch"
                    label="Bank Branch"
                    input-id="bank-branch"
                    type="text"
                    placeholder="Bank Branch"
                    :flex="true"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.creditLimit"
                    label="Credit Limit (USD)"
                    input-id="credit-limit"
                    type="number"
                    :flex="true"
                  />
                </div>
              </div>

              <!-- Row 5: Account, Shipping Method -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.accountId"
                    label="Account"
                    select-id="account-id"
                    :flex="true"
                    :options="[
                      { label: 'Select account', value: '' },
                      ...filteredAccounts.map((acc) => ({
                        label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
                        value: acc.id,
                      })),
                    ]"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.shippingMethodId"
                    label="Preferred shipping method"
                    select-id="shipping-method"
                    :flex="true"
                    :options="[
                      { label: 'Select', value: '' },
                      ...shippingMethodsList.map((method) => ({
                        label: method.name,
                        value: method.id,
                      })),
                      { label: 'Add New Shipping Method', value: '__add_new__' },
                    ]"
                    @change="handleShippingMethodChange"
                  />
                </div>
              </div>

              <!-- Row 6: Vendor Group, TAX Registration Number -->
            <div class="vendor-creation-form-group-row">
              <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.vendorGroupId"
                    label="Vendor Group"
                    select-id="vendor-group"
                    :flex="true"
                    :options="[
                      { label: 'Select', value: '' },
                      ...vendorGroups.map((group) => ({
                        label: group.name,
                        value: group.id,
                      })),
                      { label: 'Add New Group', value: '__create_new__' },
                    ]"
                    @change="handleVendorGroupChange"
                  />
                </div>
                <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                  <LabelInputField
                    v-model="form.taxRegistrationNumber"
                    label="TAX Registration Number"
                    input-id="tax-registration-number"
                    type="text"
                    placeholder="TAX Registration Number"
                    :flex="true"
                  />
                </div>
              </div>

              <!-- Row 7: Tax Code, Service tax registration no -->
              <div class="vendor-creation-form-group-row">
                <div class="vendor-creation-form-group vendor-creation-form-group--label-dropdown">
                  <LabelDropdown
                    v-model="form.taxCode"
                    label="Tax Code"
                    select-id="tax-code"
                    :flex="true"
                    :options="[{ label: 'Select', value: '' }]"
                  />
              </div>
              <div class="vendor-creation-form-group vendor-creation-form-group--label-input">
                <LabelInputField
                    v-model="form.serviceTaxRegistrationNo"
                    label="Service tax registration no"
                    input-id="service-tax-registration-no"
                    type="text"
                    placeholder="Service tax registration no"
                    :flex="true"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Contacts Tab -->
          <div
            class="vendor-creation-tab-panel"
            :class="{ 'vendor-creation-tab-panel--active': activeTab === 'contacts' }"
            :aria-hidden="activeTab !== 'contacts'"
          >
            <div class="vendor-creation-contacts-section">
              <div class="vendor-creation-contacts-header">
                <h3>Contacts</h3>
                <div class="vendor-creation-add-contact-container">
                  <PrimaryButton
                    label="Add Contact"
                    icon="bi bi-plus-circle"
                    @click="addContactRow"
                  />
                </div>
              </div>

              <!-- Contact Cards -->
              <div class="vendor-creation-contacts-grid">
                <!-- Existing Contacts -->
                <div v-for="(contact, idx) in contacts" :key="'contact-' + idx" class="vendor-creation-contact-card">
                  <div class="vendor-creation-contact-card-header">
                    <LabelCheckboxField
                      :input-id="`vcd-contact-primary-${idx}`"
                      :label="contacts.length === 1 ? 'Primary Contact *' : 'Primary Contact'"
                      v-model="contact.primary"
                      custom-class="label-checkbox-field--card-compact"
                      :disabled="contacts.length === 1"
                      @change="setPrimaryContact(idx)"
                    />
                  </div>

                  <div class="vendor-creation-contact-fields">
                    <div class="vendor-creation-contact-field-row">
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-contact-name-${idx}`"
                          label="Contact Name"
                          v-model="contact.name"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-contact-title-${idx}`"
                          label="Title"
                          v-model="contact.title"
                          flex
                        />
                      </div>
                    </div>

                    <div class="vendor-creation-contact-field-row">
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-contact-phone-${idx}`"
                          label="Business Phone"
                          v-model="contact.phone"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-contact-email-${idx}`"
                          label="Email"
                          v-model="contact.email"
                          type="email"
                          flex
                          :has-error="!!contactEmailErrors[`contact-${idx}`]"
                          @blur="validateContactEmail(contact.email, idx, false)"
                          @input="handleContactEmailInput(contact.email, idx, false)"
                        >
                          <div v-if="checkingContactEmails[`contact-${idx}`]" class="vendor-creation-checking">Checking availability...</div>
                          <div v-if="contactEmailErrors[`contact-${idx}`]" class="vendor-creation-error-text">{{ contactEmailErrors[`contact-${idx}`] }}</div>
                        </LabelInputField>
                      </div>
                    </div>
                  </div>

                  <div class="vendor-creation-contact-actions">
                    <SecondaryButton
                      label="Delete"
                      icon=""
                      custom-class="consultant-secondary-btn--danger"
                      @click="deleteContact(idx)"
                    />
                  </div>
                </div>

                <!-- New Contact Cards -->
                <div v-for="(nc, nidx) in newContacts" :key="'new-contact-' + nidx" class="vendor-creation-contact-card vendor-creation-contact-card-new">
                  <div class="vendor-creation-contact-card-header">
                    <LabelCheckboxField
                      :input-id="`vcd-new-contact-primary-${nidx}`"
                      label="Primary Contact"
                      v-model="nc.primary"
                      custom-class="label-checkbox-field--card-compact"
                      :disabled="contacts.length + newContacts.length === 1"
                      @change="setPrimaryContactNew(nidx)"
                    />
                  </div>

                  <div class="vendor-creation-contact-fields">
                    <div class="vendor-creation-contact-field-row">
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-contact-name-${nidx}`"
                          label="Contact Name"
                          v-model="nc.name"
                          placeholder="Contact Name"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-contact-title-${nidx}`"
                          label="Title"
                          v-model="nc.title"
                          placeholder="Title"
                          flex
                        />
                      </div>
                    </div>

                    <div class="vendor-creation-contact-field-row">
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-contact-phone-${nidx}`"
                          label="Business Phone"
                          v-model="nc.phone"
                          placeholder="Business Phone"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-contact-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-contact-email-${nidx}`"
                          label="Email"
                          v-model="nc.email"
                          type="email"
                          placeholder="Email"
                          flex
                          :has-error="!!contactEmailErrors[`new-contact-${nidx}`]"
                          @blur="validateContactEmail(nc.email, nidx, true)"
                          @input="handleContactEmailInput(nc.email, nidx, true)"
                        >
                          <div v-if="checkingContactEmails[`new-contact-${nidx}`]" class="vendor-creation-checking">Checking availability...</div>
                          <div v-if="contactEmailErrors[`new-contact-${nidx}`]" class="vendor-creation-error-text">{{ contactEmailErrors[`new-contact-${nidx}`] }}</div>
                        </LabelInputField>
                      </div>
                    </div>
                  </div>

                  <div class="vendor-creation-contact-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelContact(nidx)"
                    />
                    <PrimaryButton
                      label="Save"
                      icon=""
                      compact
                      @click="saveContact(nidx)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Addresses Tab -->
          <div
            class="vendor-creation-tab-panel"
            :class="{ 'vendor-creation-tab-panel--active': activeTab === 'addresses' }"
            :aria-hidden="activeTab !== 'addresses'"
          >
            <div class="vendor-creation-addresses-section">
              <div class="vendor-creation-addresses-header">
                <h3>Addresses</h3>
                <div class="vendor-creation-add-address-container">
                  <PrimaryButton
                    label="Add Address"
                    icon="bi bi-plus-circle"
                    @click="addNewAddressRow"
                  />
                </div>
              </div>

              <!-- Address Cards -->
              <div class="vendor-creation-addresses-grid">
                <!-- Existing Addresses -->
                <div v-for="(address, idx) in addresses" :key="'address-' + idx" class="vendor-creation-address-card">
                  <div class="vendor-creation-address-card-header">
                    <LabelDropdown
                      v-model="address.type"
                      label="Address Type"
                      :select-id="`vcd-address-type-${idx}`"
                      flex
                      :options="addressTypeOptions"
                    />
                  </div>

                  <div class="vendor-creation-address-fields">
                    <div class="vendor-creation-address-field-row">
                      <div class="vendor-creation-address-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-address-line1-${idx}`"
                          label="Address Line 1"
                          v-model="address.address1"
                          placeholder="Street address"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-address-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-address-line2-${idx}`"
                          label="Address Line 2"
                          v-model="address.address2"
                          placeholder="Apartment, suite, etc."
                          flex
                        />
                      </div>
                    </div>

                    <AddressSelector
                      :country="address.country"
                      :state="address.state"
                      :city="address.city"
                      :postal-code="address.postalCode"
                      @update:country="address.country = $event"
                      @update:state="address.state = $event"
                      @update:city="address.city = $event"
                      @update:postalCode="address.postalCode = $event"
                    />
                  </div>

                  <div class="vendor-creation-address-actions">
                    <SecondaryButton
                      label="Delete"
                      icon=""
                      custom-class="consultant-secondary-btn--danger"
                      @click="deleteAddress(idx)"
                    />
                  </div>
                </div>

                <!-- New Address Cards -->
                <div v-for="(na, nidx) in newAddresses" :key="'new-address-' + nidx" class="vendor-creation-address-card vendor-creation-address-card-new">
                  <div class="vendor-creation-address-card-header">
                    <LabelDropdown
                      v-model="na.type"
                      label="Address Type"
                      :select-id="`vcd-new-address-type-${nidx}`"
                      flex
                      :options="addressTypeOptions"
                    />
                  </div>

                  <div class="vendor-creation-address-fields">
                    <div class="vendor-creation-address-field-row">
                      <div class="vendor-creation-address-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-address-line1-${nidx}`"
                          label="Address Line 1"
                          v-model="na.address1"
                          placeholder="Street address"
                          flex
                        />
                      </div>
                      <div class="vendor-creation-address-field vendor-creation-form-group--label-input">
                        <LabelInputField
                          :input-id="`vcd-new-address-line2-${nidx}`"
                          label="Address Line 2"
                          v-model="na.address2"
                          placeholder="Apartment, suite, etc."
                          flex
                        />
                      </div>
                    </div>

                    <AddressSelector
                      :country="na.country"
                      :state="na.state"
                      :city="na.city"
                      :postal-code="na.postalCode"
                      @update:country="na.country = $event"
                      @update:state="na.state = $event"
                      @update:city="na.city = $event"
                      @update:postalCode="na.postalCode = $event"
                    />
                  </div>

                  <div class="vendor-creation-address-actions">
                    <SecondaryButton
                      label="Cancel"
                      icon="bi bi-x-lg"
                      @click="cancelNewAddress(nidx)"
                    />
                    <PrimaryButton
                      label="Save"
                      icon=""
                      compact
                      @click="saveNewAddress(nidx)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Tab -->
          <div
            class="vendor-creation-tab-panel"
            :class="{ 'vendor-creation-tab-panel--active': activeTab === 'documents' }"
            :aria-hidden="activeTab !== 'documents'"
          >
            <div class="vendor-creation-documents-section">
              <div class="vendor-creation-documents-header">
                <h3>Documents</h3>
                <div class="vendor-creation-add-address-container">
                  <PrimaryButton
                    label="Add Document"
                    icon="bi bi-plus-circle"
                    @click="triggerAddDocument"
                  />
                  <input
                    id="add-vendor-document-input"
                    ref="addDocumentFileInput"
                    type="file"
                    multiple
                    style="display: none;"
                    @change="handleDocumentFileUpload"
                  />
                </div>
              </div>
              
              <!-- Split View: Documents List (Left) and Preview (Right) -->
              <div class="vendor-creation-documents-split-view">
                <!-- Left Side: Documents List -->
                <div class="vendor-creation-documents-list-container">
                  <div v-if="documents.length > 0" class="vendor-creation-documents-list">
                    <div 
                      v-for="(document, index) in documents" 
                      :key="index" 
                      class="vendor-creation-document-item"
                      :class="{ 'active': selectedDocumentIndex === index }"
                      @click="selectDocument(index)"
                    >
                      <div class="vendor-creation-document-info">
                        <div class="vendor-creation-document-name">
                          <i class="bi bi-file-earmark"></i> {{ document.fileName }}
                        </div>
                        <div class="vendor-creation-document-meta">
                          <span class="vendor-creation-document-size">{{ formatFileSize(document.fileSize) }}</span>
                          <span class="vendor-creation-document-date">{{ formatDate(document.uploadedAt) }}</span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        @click.stop="removeVendorDocument(index)" 
                        class="vendor-creation-document-delete-btn" 
                        title="Remove document"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                  <div v-else class="vendor-creation-no-documents-message">
                    <div class="vendor-creation-no-documents-icon">📄</div>
                    <p>No documents added yet</p>
                    <p class="vendor-creation-tooltip-subtitle">Click "Add Document" to upload files</p>
                  </div>
                </div>
                
                <!-- Right Side: Preview -->
                <div class="vendor-creation-document-preview-container">
                  <div v-if="selectedDocumentIndex !== null && documents[selectedDocumentIndex]" class="vendor-creation-document-preview">
                    <div class="vendor-creation-document-preview-header">
                      <h4>{{ documents[selectedDocumentIndex].fileName }}</h4>
                    </div>
                    <div class="vendor-creation-document-preview-content">
                      <div v-if="loadingPreview" class="vendor-creation-document-preview-loading">
                        <div class="vendor-creation-document-preview-icon">
                          <i class="bi bi-arrow-repeat" style="font-size: 2rem; color: #0099cc; animation: spin 1s linear infinite;"></i>
                        </div>
                        <p>Loading preview...</p>
                      </div>
                      <div v-else-if="currentPreviewUrl && isImageFile(documents[selectedDocumentIndex])" class="vendor-creation-document-preview-image">
                        <img :src="currentPreviewUrl" :alt="documents[selectedDocumentIndex].fileName" @error="handlePreviewError" />
                      </div>
                      <div v-else-if="currentPreviewUrl && isPdfFile(documents[selectedDocumentIndex])" class="vendor-creation-document-preview-pdf">
                        <iframe :src="currentPreviewUrl" frameborder="0" @error="handlePreviewError"></iframe>
                      </div>
                      <div v-else-if="previewError" class="vendor-creation-document-preview-error">
                        <div class="vendor-creation-document-preview-icon">
                          <i class="bi bi-exclamation-triangle" style="font-size: 4rem; color: #dc3545;"></i>
                        </div>
                        <p>Failed to load preview</p>
                        <PrimaryButton
                          label="Open in New Window"
                          :icon="null"
                          @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                        />
                      </div>
                      <div v-else class="vendor-creation-document-preview-unsupported">
                        <div class="vendor-creation-document-preview-icon">
                          <i class="bi bi-file-earmark" style="font-size: 4rem; color: #ccc;"></i>
                        </div>
                        <p>Preview not available for this file type</p>
                        <PrimaryButton
                          label="Open in New Window"
                          :icon="null"
                          @click="openDocumentInNewWindow(documents[selectedDocumentIndex])"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-else class="vendor-creation-document-preview-empty">
                    <div class="vendor-creation-document-preview-icon">
                      <i class="bi bi-file-earmark-text" style="font-size: 4rem; color: #ccc;"></i>
                    </div>
                    <p>Select a document to preview</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Display -->
          <div v-if="generalError" class="vendor-creation-general-error">
            {{ generalError }}
          </div>
        </form>
        </div>
      </div>

      <div class="vendor-creation-dialog-footer">
        <SecondaryButton
          label="Cancel"
          icon="bi bi-x-lg"
          @click="handleClose"
        />
        <PrimaryButton
          v-if="activeTab !== 'documents'"
          label="Next"
          icon="bi bi-arrow-right"
          :disabled="isSubmitting || hasCurrentTabErrors"
          @click="handleNext"
        />
        <PrimaryButton
          v-else
          :label="isSubmitting ? 'Creating...' : 'Create Vendor'"
          :icon="null"
          :disabled="isSubmitting"
          :loading="isSubmitting"
          @click="handleSubmit"
        />
      </div>
    </div>

    <!-- Modals -->
    <AddNewVendorGroup
      :visible="showAddGroupModal"
      :companyId="companyId"
      @ok="onAddGroupOk"
      @cancel="showAddGroupModal = false"
    />
    <AddPreferredShippingMethod
      :visible="showAddShippingMethodModal"
      :companyId="companyId"
      @ok="onAddShippingMethodOk"
      @cancel="showAddShippingMethodModal = false"
    />
    <AddNewPaymentTerm
      :visible="showAddPaymentTermModal"
      :companyId="companyId"
      @ok="onAddPaymentTermOk"
      @cancel="showAddPaymentTermModal = false"
    />
    
    <!-- Error Modal -->
    <ErrorModal 
      :show="showErrorModal" 
      :title="errorTitle" 
      :message="errorMessage" 
      @close="closeErrorModal" 
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      button-text="Yes"
      secondary-button-text="No"
      @close="stayOnPageAfterSave"
      @secondary="leavePageAfterSave"
    />
  </div>
  </Teleport>
</template>

<script>
import '../styles/VendorCreationDialog.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import CompanyService from '@/services/company.service.js';
import vendorService from '@/services/vendor.service.js';
import TabNavigation from '@/components/TabNavigation.vue';
import AddressSelector from '@/components/AddressSelector.vue';
import AddNewVendorGroup from '@/modules/company/views/AddNewVendorGroup.vue';
import AddNewPaymentTerm from '@/modules/company/views/AddNewPaymentTerm.vue';
import AddPreferredShippingMethod from '@/modules/vendor/views/AddPreferredShippingMethod.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';

import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import { VENDOR_ROLES_FOR_SELECT } from '@/constants/vendorRoles.js';

export default {
  name: 'VendorCreationDialog',
  components: {
    TabNavigation,
    AddressSelector,
    AddNewVendorGroup,
    AddNewPaymentTerm,
    AddPreferredShippingMethod,
    ErrorModal,
    SuccessModal,
    LabelInputField,
    LabelCheckboxField,
    LabelDropdown,
    LabelDescriptionField,
    LabelDateField,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: String,
      required: true
    },
    initialVendorName: {
      type: String,
      default: ''
    },
    initialVendorId: {
      type: String,
      default: ''
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activeTab: 'general',
      tabs: [
        { id: 'general', name: 'Basic Information' },
        { id: 'details', name: 'Payment & Settings' },
        { id: 'contacts', name: 'Contact Details' },
        { id: 'addresses', name: 'Address Information' },
        { id: 'documents', name: 'Documents' }
      ],
      form: {
        vendorId: '',
        vendorName: '',
        vendorRole: '',
        active: true,
        vendorSince: this.getCurrentDate(),
        taxId: '',
        openingBalance: 0,
        balanceAsOf: this.getCurrentDate(),
        balance: 0,
        fax: '',
        webpage: '',
        email: '',
        phone: '',
        notes: '',
        accountId: '',
        creditLimit: 0,
        shippingMethodId: '',
        paymentMethod: '',
        paymentTermId: '',
        accountNumber: '',
        bankName: '',
        bankBranch: '',
        bankAccountType: '',
        bankRoutingNumber: '',
        vendorGroupId: '',
        taxRegistrationNumber: '',
        taxCode: '',
        serviceTaxRegistrationNo: ''
      },
      contacts: [],
      newContacts: [],
      addresses: [],
      newAddresses: [],
      filteredAccounts: [],
      paymentTermsList: [],
      shippingMethodsList: [],
      vendorGroups: [],
      previousShippingMethod: '',
      previousPaymentTerm: '',
      previousVendorGroup: '',
      showAddGroupModal: false,
      showAddPaymentTermModal: false,
      showAddShippingMethodModal: false,
      validationErrors: {},
      generalError: '',
      isSubmitting: false,
      showErrorModal: false,
      errorTitle: 'Validation Error',
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      createdVendor: null,
      vendorIdExists: false,
      checkingVendorId: false,
      vendorIdCheckTimeout: null,
      contactEmailErrors: {},
      checkingContactEmails: {},
      contactEmailCheckTimeouts: {},
      documents: [],
      selectedDocumentIndex: null,
      documentPreviewUrls: {}, // Cache for preview URLs
      currentPreviewUrl: '', // Current preview URL for selected document
      loadingPreview: false,
      previewError: false,
      addressTypeOptions: [
        { label: 'Select Type', value: '' },
        { label: 'Billing', value: 'Billing' },
        { label: 'Shipping', value: 'Shipping' },
        { label: 'Other', value: 'Other' },
      ],
    };
  },
  methods: {
    triggerAddDocument() {
      const input = this.$refs.addDocumentFileInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    getCurrentDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    handleTabChange(newTab) {
      this.activeTab = newTab;
      
      // Initialize default rows when switching to contacts tab
      if (newTab === 'contacts') {
        this.initializeDefaultContacts();
      }
      
      // Initialize default rows when switching to addresses tab
      if (newTab === 'addresses') {
        this.initializeDefaultAddresses();
      }
    },
    initializeDefaultContacts() {
      // If no contacts exist, add one default contact row
      if (this.contacts.length === 0 && this.newContacts.length === 0) {
        this.newContacts.push({
          primary: true,  // First contact must be primary
          name: '',
          title: '',
          phone: '',
          email: ''
        });
      }
    },
    initializeDefaultAddresses() {
      // If no addresses exist, add one default address row with Billing type
      if (this.addresses.length === 0 && this.newAddresses.length === 0) {
        this.newAddresses.push({
          type: 'Billing',
          address1: '',
          address2: '',
          city: '',
          state: 'UT',  // Default to Utah
          postalCode: '',
          country: 'US'  // Default to United States
        });
      }
    },
    handleOverlayClick(event) {
      if (this.closeOnOverlayClick && event.target === event.currentTarget) {
        this.handleClose();
      }
    },
    handleClose() {
      this.$emit('close');
      this.$emit('cancel');
      this.resetForm();
    },
    clearFieldError(field) {
      if (this.validationErrors[field]) {
        delete this.validationErrors[field];
      }
      this.generalError = '';
    },
    
    async handleVendorIdInput() {
      this.vendorIdExists = false;
      
      // Only check if vendor ID is not empty and company ID exists
      if (!this.form.vendorId || !this.form.vendorId.trim()) {
        return;
      }
      
      if (!this.companyId) {
        return;
      }
      
      // Debounce the API call
      clearTimeout(this.vendorIdCheckTimeout);
      this.checkingVendorId = true;
      
      this.vendorIdCheckTimeout = setTimeout(async () => {
        try {
          const response = await vendorService.checkVendorIdExists(this.form.vendorId.trim(), this.companyId);
          this.vendorIdExists = response.data.exists;
        } catch (error) {
          logger.error('Error checking vendor ID:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingVendorId = false;
        }
      }, 500); // 500ms debounce
    },
    toggleActive() {
      this.form.active = !this.form.active;
    },
    handleNext() {
      // Validate current tab before proceeding
      if (!this.validateCurrentTab()) {
        return;
      }
      
      const tabs = ['general', 'details', 'contacts', 'addresses', 'documents'];
      const currentIndex = tabs.indexOf(this.activeTab);
      
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
      }
    },
    validateCurrentTab() {
      if (this.activeTab === 'general') {
        if (!this.form.vendorId || this.form.vendorId.trim() === '') {
          this.validationErrors.vendorId = 'Vendor ID is required.';
          this.generalError = 'Vendor ID is required.';
          return false;
        }
        if (this.vendorIdExists) {
          this.validationErrors.vendorId = 'This Vendor ID already exists in this company.';
          this.generalError = 'This Vendor ID already exists in this company.';
          return false;
        }
        if (!this.form.vendorName || this.form.vendorName.trim() === '') {
          this.validationErrors.vendorName = 'Vendor Name is required.';
          this.generalError = 'Vendor Name is required.';
          return false;
        }
        // Clear errors if validation passes
        this.generalError = '';
        delete this.validationErrors.vendorId;
        delete this.validationErrors.vendorName;
        return true;
      }
      
      if (this.activeTab === 'details') {
        if (!this.form.paymentTermId) {
          this.validationErrors.paymentTermId = 'Payment Terms is required.';
          this.generalError = 'Payment Terms is required.';
          return false;
        }
        if (!this.form.paymentMethod) {
          this.validationErrors.paymentMethod = 'Payment Method is required.';
          this.generalError = 'Payment Method is required.';
          return false;
        }
        // Clear errors if validation passes
        this.generalError = '';
        delete this.validationErrors.paymentTermId;
        delete this.validationErrors.paymentMethod;
        return true;
      }
      
      if (this.activeTab === 'contacts') {
        const allContacts = [...this.contacts, ...this.newContacts];
        const hasFilledContact = allContacts.some(contact =>
          (contact.name && String(contact.name).trim()) ||
          (contact.email && String(contact.email).trim()) ||
          (contact.phone && String(contact.phone).trim()) ||
          (contact.title && String(contact.title).trim())
        );

        if (hasFilledContact && !allContacts.some(contact => contact.primary)) {
          this.generalError = 'At least one contact must be marked as primary.';
          return false;
        }

        this.generalError = '';
        return true;
      }
      
      if (this.activeTab === 'addresses') {
        this.generalError = '';
        return true;
      }
      
      return true;
    },
    // Contact Methods
    addContactRow() {
      if (this.newContacts.length === 0) {
        this.newContacts.push({
          primary: this.contacts.length === 0,
          name: '',
          title: '',
          phone: '',
          email: ''
        });
      }
    },
    async saveContact(nidx) {
      const contact = this.newContacts[nidx];
      
      // Validate required fields
      if (!contact.name || !contact.title || !contact.phone || !contact.email) {
        this.showError('Contact Validation Error', 'Please fill in all contact fields before saving.');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.email.trim())) {
        this.showError('Contact Validation Error', 'Please enter a valid email address.');
        return;
      }
      
      // Validate email uniqueness (immediate check, no debounce)
      if (contact.email && contact.email.trim()) {
        await this.validateContactEmail(contact.email, nidx, true, true);
        const emailErrorKey = `new-contact-${nidx}`;
        if (this.contactEmailErrors[emailErrorKey]) {
          this.showError('Contact Email Validation Error', this.contactEmailErrors[emailErrorKey]);
          return;
        }
      }
      
      // Handle primary contact logic
      if (this.contacts.length === 0) {
        contact.primary = true;
      } else if (contact.primary) {
        this.contacts.forEach(c => c.primary = false);
        this.newContacts.forEach((c, i) => {
          if (i !== nidx) c.primary = false;
        });
      }
      
      this.contacts.push(contact);
      this.newContacts.splice(nidx, 1);
      
      // Update vendor's email and phone if this is the primary contact
      if (contact.primary) {
        this.form.email = contact.email || '';
        this.form.phone = contact.phone || '';
      }
      
      this.generalError = '';
    },
    cancelContact(nidx) {
      this.newContacts.splice(nidx, 1);
    },
    deleteContact(idx) {
      const wasPrimary = this.contacts[idx] && this.contacts[idx].primary;
      this.contacts.splice(idx, 1);
      
      // Clear vendor's email and phone if primary contact was deleted
      if (wasPrimary) {
        this.form.email = '';
        this.form.phone = '';
      }
    },
    setPrimaryContact(idx) {
      const contact = this.contacts[idx];
      
      if (contact.primary) {
        this.newContacts.forEach(c => c.primary = false);
        this.contacts.forEach((c, i) => {
          if (i !== idx) c.primary = false;
        });
        
        // Update vendor's email and phone
        this.form.email = contact.email || '';
        this.form.phone = contact.phone || '';
      }
    },
    setPrimaryContactNew(nidx) {
      const contact = this.newContacts[nidx];
      
      if (contact.primary) {
        this.contacts.forEach(c => c.primary = false);
        this.newContacts.forEach((c, i) => {
          if (i !== nidx) c.primary = false;
        });
      }
    },
    // Address Methods
    addNewAddressRow() {
      if (this.newAddresses.length === 0) {
        this.newAddresses.push({ 
          type: 'Billing', 
          address1: '', 
          address2: '', 
          city: '', 
          state: 'UT',  // Default to Utah
          postalCode: '', 
          country: 'US'  // Default to United States
        });
      }
    },
    saveNewAddress(idx) {
      const na = this.newAddresses[idx];
      this.addresses.push(na);
      this.newAddresses.splice(idx, 1);
    },
    cancelNewAddress(idx) {
      this.newAddresses.splice(idx, 1);
    },
    deleteAddress(idx) {
      this.addresses.splice(idx, 1);
    },
    getMainAddress() {
      // Priority: Billing address > first address > null
      const billingAddress = this.addresses.find(addr => addr.type === 'Billing');
      const mainAddr = billingAddress || this.addresses[0];
      
      if (!mainAddr) {
        return { address: null, addressDetails: null };
      }
      
      // Format address details into a single string
      const addressParts = [
        mainAddr.address1,
        mainAddr.address2,
        mainAddr.city,
        mainAddr.state,
        mainAddr.postalCode,
        mainAddr.country
      ].filter(part => part && part.trim() !== '');
      
      return {
        address: mainAddr.type,
        addressDetails: addressParts.join('\n')
      };
    },
    // Shipping Method Handlers
    handleShippingMethodChange(selected) {
      if (selected === '__add_new__') {
        this.showAddShippingMethodModal = true;
        this.$nextTick(() => {
          this.form.shippingMethodId = this.previousShippingMethod;
        });
      } else {
        this.form.shippingMethodId = selected;
        this.previousShippingMethod = selected;
      }
    },
    async onAddShippingMethodOk(newMethod) {
      this.showAddShippingMethodModal = false;
      await this.fetchShippingMethods();
      if (newMethod && newMethod.id) {
        this.form.shippingMethodId = newMethod.id;
        this.previousShippingMethod = newMethod.id;
        if (this.$toast) {
          this.$toast.success('Shipping method created!');
        }
      }
    },
    // Payment Term Handlers
    handlePaymentTermChange(selected) {
      if (selected === '__add_new__') {
        this.showAddPaymentTermModal = true;
        this.$nextTick(() => {
          this.form.paymentTermId = this.previousPaymentTerm;
        });
      } else {
        this.form.paymentTermId = selected;
        this.previousPaymentTerm = selected;
        this.clearFieldError('paymentTermId');
      }
    },
    async onAddPaymentTermOk(newTerm) {
      this.showAddPaymentTermModal = false;
      await this.fetchPaymentTerms();
      if (newTerm && newTerm.id) {
        this.form.paymentTermId = newTerm.id;
        this.previousPaymentTerm = newTerm.id;
        if (this.$toast) {
          this.$toast.success('Payment term created!');
        }
      }
    },
    // Vendor Group Handlers
    handleVendorGroupChange(selected) {
      if (selected === '__create_new__') {
        this.showAddGroupModal = true;
        this.$nextTick(() => {
          this.form.vendorGroupId = this.previousVendorGroup;
        });
      } else {
        this.form.vendorGroupId = selected;
        this.previousVendorGroup = selected;
      }
    },
    async onAddGroupOk(group) {
      this.showAddGroupModal = false;
      await this.fetchVendorGroups();
      if (group && group.id) {
        this.form.vendorGroupId = group.id;
        this.previousVendorGroup = group.id;
        if (this.$toast) {
          this.$toast.success('Vendor group created!');
        }
      }
    },
    validateForm() {
      this.validationErrors = {};
      let isValid = true;

      // Vendor ID is required
      if (!this.form.vendorId || this.form.vendorId.trim() === '') {
        this.validationErrors.vendorId = 'Vendor ID is required.';
        isValid = false;
      }
      
      // Check if vendor ID already exists
      if (this.vendorIdExists) {
        this.validationErrors.vendorId = 'This Vendor ID already exists in this company.';
        isValid = false;
      }

      // Vendor Name is required
      if (!this.form.vendorName || this.form.vendorName.trim() === '') {
        this.validationErrors.vendorName = 'Vendor Name is required.';
        isValid = false;
      }

      // Payment Terms is required
      if (!this.form.paymentTermId) {
        this.validationErrors.paymentTermId = 'Payment Terms is required.';
        isValid = false;
      }

      // Payment Method is required
      if (!this.form.paymentMethod) {
        this.validationErrors.paymentMethod = 'Payment Method is required.';
        isValid = false;
      }

      const allContacts = [...this.contacts, ...this.newContacts];
      const hasFilledContact = allContacts.some(contact =>
        (contact.name && String(contact.name).trim()) ||
        (contact.email && String(contact.email).trim()) ||
        (contact.phone && String(contact.phone).trim()) ||
        (contact.title && String(contact.title).trim())
      );
      if (hasFilledContact && !allContacts.some(contact => contact.primary)) {
        this.generalError = 'At least one contact must be marked as primary.';
        isValid = false;
      }

      return isValid;
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }
      
      // Check for contact email errors before saving
      const hasContactEmailErrors = Object.keys(this.contactEmailErrors).length > 0;
      if (hasContactEmailErrors) {
        const emailErrors = Object.values(this.contactEmailErrors);
        this.showError('Contact Email Validation Error', emailErrors.join('\n'));
        this.activeTab = 'contacts';
        return;
      }

      this.isSubmitting = true;
      this.generalError = '';

      try {
        // Get main address and address details
        const mainAddress = this.getMainAddress();
        
        const payload = {
          vendorId: this.form.vendorId.trim(),
          vendorName: this.form.vendorName.trim(),
          vendorRole: this.form.vendorRole || null,
          active: this.form.active,
          vendorSince: this.form.vendorSince,
          taxId: this.form.taxId.trim() || null,
          openingBalance: this.form.openingBalance || 0,
          balanceAsOf: this.form.balanceAsOf,
          balance: this.form.balance || 0,
          fax: this.form.fax.trim() || null,
          webpage: this.form.webpage.trim() || null,
          email: this.form.email.trim() || null,
          phone: this.form.phone.trim() || null,
          notes: this.form.notes.trim() || null,
          accountId: this.form.accountId || null,
          creditLimit: this.form.creditLimit || 0,
          shippingMethodId: this.form.shippingMethodId || null,
          paymentMethod: this.form.paymentMethod || null,
          paymentTermId: this.form.paymentTermId || null,
          accountNumber: this.form.accountNumber.trim() || null,
          bankName: this.form.bankName.trim() || null,
          bankBranch: this.form.bankBranch.trim() || null,
          bankAccountType: this.form.bankAccountType || null,
          bankRoutingNumber: this.form.bankRoutingNumber.trim() || null,
          vendorGroupId: this.form.vendorGroupId || null,
          taxRegistrationNumber: this.form.taxRegistrationNumber.trim() || null,
          taxCode: this.form.taxCode || null,
          serviceTaxRegistrationNo: this.form.serviceTaxRegistrationNo.trim() || null,
          companyId: this.companyId,
          contacts: this.contacts,
          addresses: this.addresses,
          address: mainAddress.address,
          addressDetails: mainAddress.addressDetails
        };

        const response = await ApiService.post('/vendor/vendors', payload);
        const savedVendor = response.data;
        
        if (savedVendor && savedVendor.id) {
          // Upload documents if any
          if (this.documents.length > 0) {
            await this.uploadDocuments(savedVendor.id);
          }
          
          this.createdVendor = savedVendor;
          this.successMessage = 'Vendor created successfully.\nStay on this page to view the details?';
          this.showSuccessModal = true;
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        logger.error('Error creating vendor:', error);
        
        let errorMessage = '';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Failed to create vendor. Please try again.';
        }
        
        // Handle email-related errors
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          if (errorMessage.includes('consultant')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a consultant in this company.');
          } else if (errorMessage.includes('customer contact') || errorMessage.includes('contact')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for a customer contact in this company.');
          } else if (errorMessage.includes('vendor')) {
            this.showError('Contact Email Validation Error', 'This email address already exists for another vendor or vendor contact in this company.');
          } else {
            this.showError('Contact Email Validation Error', errorMessage);
          }
          this.activeTab = 'contacts';
        } else {
          this.showError('Save Failed', errorMessage);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    handleContactEmailInput(email, idx, isNew) {
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      this.clearContactEmailError(key);
      
      if (!email || !email.trim()) {
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // Debounce the validation
      this.contactEmailCheckTimeouts[key] = setTimeout(() => {
        this.validateContactEmail(email, idx, isNew);
      }, 500);
    },
    async validateContactEmail(email, idx, isNew, immediate = false) {
      if (!email || !email.trim()) {
        return;
      }
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
        this.contactEmailErrors[key] = 'Please enter a valid email address.';
        this.checkingContactEmails[key] = false;
        return;
      }
      
      const key = isNew ? `new-contact-${idx}` : `contact-${idx}`;
      
      if (!this.companyId) {
        this.checkingContactEmails[key] = false;
        return;
      }
      
      // Clear previous timeout
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      }
      
      // If immediate, check right away; otherwise debounce
      const performCheck = async () => {
        this.checkingContactEmails[key] = true;
        delete this.contactEmailErrors[key];
        
        try {
          const response = await vendorService.checkVendorEmailExists(email.trim(), this.companyId);
          
          if (response.data.exists) {
            let errorMessage = '';
            if (response.data.existsAsConsultant) {
              errorMessage = 'This email address already exists for a consultant in this company.';
            } else if (response.data.existsAsContact) {
              errorMessage = 'This email address already exists for a customer contact in this company.';
            } else if (response.data.existsAsVendor) {
              errorMessage = 'This email address already exists for another vendor or vendor contact in this company.';
            } else {
              errorMessage = 'This email address is already in use.';
            }
            this.contactEmailErrors[key] = errorMessage;
          }
        } catch (error) {
          logger.error('Error checking contact email:', error);
          // Don't show error to user for validation check failures
        } finally {
          this.checkingContactEmails[key] = false;
        }
      };
      
      if (immediate) {
        await performCheck();
      } else {
        this.contactEmailCheckTimeouts[key] = setTimeout(performCheck, 500);
      }
    },
    clearContactEmailError(key) {
      delete this.contactEmailErrors[key];
      if (this.contactEmailCheckTimeouts[key]) {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
        delete this.contactEmailCheckTimeouts[key];
      }
      this.checkingContactEmails[key] = false;
    },
    showError(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Validation Error';
      this.errorMessage = '';
    },
    stayOnPageAfterSave() {
      const created = this.createdVendor;
      this.showSuccessModal = false;
      this.successMessage = '';
      if (created && created.id && this.companyId) {
        this.$emit('ok', created);
        this.$emit('close');
        this.$router.push(`/company/${this.companyId}/dashboard/vendors/edit/${created.id}`);
      }
    },
    leavePageAfterSave() {
      const created = this.createdVendor;
      this.showSuccessModal = false;
      this.successMessage = '';
      this.resetForm();
      this.createdVendor = null;
      this.$emit('ok', created);
      this.$emit('close');
    },
    setInitialVendorData() {
      if (this.initialVendorName && this.initialVendorName.trim() && this.visible) {
        const vendorName = this.initialVendorName.trim();
        // Set vendor name immediately
        this.form.vendorName = vendorName;
        
        // Format vendor ID: Vendor Name-Orig ID
        if (this.initialVendorId && this.initialVendorId.trim()) {
          // Combine vendor name with Orig ID: "Vendor Name-Orig ID"
          const origId = this.initialVendorId.trim();
          // Clean vendor name for ID (remove special characters, keep spaces as hyphens)
          const cleanName = vendorName.replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-');
          this.form.vendorId = `${cleanName}-${origId}`;
        } else {
          // Auto-generate vendor ID from vendor name if Orig ID not available
          const cleanName = vendorName.replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-');
          this.form.vendorId = cleanName;
        }
      }
    },
    resetForm() {
      this.activeTab = 'general';
      this.form = {
        vendorId: '',
        vendorName: '',
        vendorRole: '',
        active: true,
        vendorSince: this.getCurrentDate(),
        taxId: '',
        openingBalance: 0,
        balanceAsOf: this.getCurrentDate(),
        balance: 0,
        fax: '',
        webpage: '',
        email: '',
        phone: '',
        notes: '',
        accountId: '',
        creditLimit: 0,
        shippingMethodId: '',
        paymentMethod: '',
        paymentTermId: '',
        accountNumber: '',
        bankName: '',
        bankBranch: '',
        bankAccountType: '',
        bankRoutingNumber: '',
        vendorGroupId: '',
        taxRegistrationNumber: '',
        taxCode: '',
        serviceTaxRegistrationNo: ''
      };
      this.contacts = [];
      this.newContacts = [];
      this.addresses = [];
      this.newAddresses = [];
      this.previousShippingMethod = '';
      this.previousPaymentTerm = '';
      this.previousVendorGroup = '';
      this.validationErrors = {};
      this.generalError = '';
      this.showErrorModal = false;
      this.errorTitle = 'Validation Error';
      this.errorMessage = '';
      this.vendorIdExists = false;
      this.checkingVendorId = false;
      clearTimeout(this.vendorIdCheckTimeout);
      this.contactEmailErrors = {};
      this.checkingContactEmails = {};
      Object.keys(this.contactEmailCheckTimeouts).forEach(key => {
        clearTimeout(this.contactEmailCheckTimeouts[key]);
      });
      this.contactEmailCheckTimeouts = {};
      // Reset documents
      this.documents = [];
      this.selectedDocumentIndex = null;
      this.documentPreviewUrls = {};
      this.currentPreviewUrl = '';
      this.loadingPreview = false;
      this.previewError = false;
    },
    async fetchCompanyAccounts() {
      if (!this.companyId) {
        logger.warn('Cannot fetch company accounts: companyId is not set');
        this.filteredAccounts = [];
        return;
      }
      
      try {
        const res = await ApiService.getCompanyAccounts(this.companyId);
        // Handle different response formats
        let allAccounts = [];
        if (res && res.data) {
          if (Array.isArray(res.data.accounts)) {
            allAccounts = res.data.accounts;
          } else if (Array.isArray(res.data.data)) {
            allAccounts = res.data.data;
          } else if (Array.isArray(res.data)) {
            allAccounts = res.data;
          }
        } else if (Array.isArray(res)) {
          allAccounts = res;
        }
        
        const allowedTypes = [
          'Expense', 'Other Expense', 'Cost of Goods Sold', 'Cash', 'Bank', 'Credit Card', 'Current Liability', 'Paypal'
        ];
        this.filteredAccounts = allAccounts.filter(a => a && allowedTypes.includes(a.accountType));
        logger.log(`Loaded ${this.filteredAccounts.length} accounts for vendor creation`);
      } catch (e) {
        logger.error('Error fetching company accounts:', e);
        this.filteredAccounts = [];
        // Don't show error toast as this is a background operation
      }
    },
    async fetchPaymentTerms() {
      try {
        const res = await ApiService.get(`/company/payment-terms?companyId=${this.companyId}`);
        this.paymentTermsList = Array.isArray(res.data.paymentTerms) ? res.data.paymentTerms : [];
      } catch (err) {
        this.paymentTermsList = [];
      }
    },
    async fetchShippingMethods() {
      try {
        const res = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${this.companyId}`);
        this.shippingMethodsList = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.shippingMethodsList = [];
      }
    },
    async fetchVendorGroups() {
      try {
        const groups = await CompanyService.getAllVendorGroups(this.companyId);
        this.vendorGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.vendorGroups = [];
      }
    },
    // Document handling methods
    handleDocumentFileUpload(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);
      let processedCount = 0;
      const totalFiles = fileArray.length;

      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const getMimeType = (filename, fallbackType) => {
            if (fallbackType && fallbackType !== '') return fallbackType;
            const extension = filename.split('.').pop().toLowerCase();
            const mimeMap = {
              'pdf': 'application/pdf',
              'doc': 'application/msword',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xls': 'application/vnd.ms-excel',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'txt': 'text/plain'
            };
            return mimeMap[extension] || 'application/octet-stream';
          };

          const document = {
            fileName: file.name,
            fileSize: file.size,
            filePath: e.target.result,
            mimeType: getMimeType(file.name, file.type),
            uploadedAt: new Date().toISOString(),
            file: file // Store the actual file for upload
          };
          this.documents.push(document);
          processedCount++;
          
          // Auto-select the last document when all files are processed
          if (processedCount === totalFiles) {
            this.$nextTick(async () => {
              this.selectedDocumentIndex = this.documents.length - 1;
              await this.selectDocument(this.selectedDocumentIndex);
            });
          } else if (this.selectedDocumentIndex === null && processedCount === 1) {
            // Select first document if none selected
            this.$nextTick(async () => {
              this.selectedDocumentIndex = 0;
              await this.selectDocument(0);
            });
          }
        };
        reader.readAsDataURL(file);
      });

      if (this.$refs.addDocumentFileInput) {
        this.$refs.addDocumentFileInput.value = '';
      }
    },
    removeVendorDocument(index) {
      if (this.documents && this.documents.length > index) {
        // Remove preview URL from cache and revoke blob URL if needed
        const document = this.documents[index];
        if (document && document.filePath) {
          const cachedUrl = this.documentPreviewUrls[document.filePath];
          if (cachedUrl && cachedUrl.startsWith('blob:')) {
            window.URL.revokeObjectURL(cachedUrl);
          }
          delete this.documentPreviewUrls[document.filePath];
        }
        
        // If removing the selected document, clear selection and preview
        if (this.selectedDocumentIndex === index) {
          this.selectedDocumentIndex = null;
          this.currentPreviewUrl = '';
          this.previewError = false;
        } else if (this.selectedDocumentIndex > index) {
          // Adjust selected index if document before it was removed
          this.selectedDocumentIndex--;
        }
        
        this.documents.splice(index, 1);
      }
    },
    async selectDocument(index) {
      this.selectedDocumentIndex = index;
      this.previewError = false;
      this.currentPreviewUrl = '';
      
      const document = this.documents[index];
      if (!document) return;
      
      // Check if URL is already cached
      if (this.documentPreviewUrls[document.filePath]) {
        this.currentPreviewUrl = this.documentPreviewUrls[document.filePath];
        return;
      }
      
      // Load preview URL
      this.loadingPreview = true;
      try {
        const url = await this.getDocumentPreviewUrl(document);
        if (url) {
          this.currentPreviewUrl = url;
        } else {
          this.previewError = true;
        }
      } catch (error) {
        logger.error('Error loading preview:', error);
        this.previewError = true;
      } finally {
        this.loadingPreview = false;
      }
    },
    handlePreviewError() {
      this.previewError = true;
      this.currentPreviewUrl = '';
    },
    async getDocumentPreviewUrl(document) {
      if (!document || !document.filePath) return '';
      
      // If already cached, return cached URL
      if (this.documentPreviewUrls[document.filePath]) {
        return this.documentPreviewUrls[document.filePath];
      }
      
      // If it's a new document (data URL), use it directly
      if (document.filePath.startsWith('data:')) {
        this.documentPreviewUrls[document.filePath] = document.filePath;
        return document.filePath;
      }
      
      // For saved documents, fetch from backend
      if (document.id) {
        try {
          if (!this.companyId) return '';
          
          const response = await vendorService.viewVendorDocument(document.id, this.companyId);
          const blob = new Blob([response.data], { type: document.mimeType || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          this.documentPreviewUrls[document.filePath] = url;
          return url;
        } catch (error) {
          logger.error('Failed to load document preview:', error);
          return '';
        }
      }
      
      return '';
    },
    isImageFile(document) {
      if (!document || !document.mimeType) return false;
      const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      return imageTypes.includes(document.mimeType.toLowerCase());
    },
    isPdfFile(document) {
      if (!document || !document.mimeType) return false;
      return document.mimeType.toLowerCase() === 'application/pdf';
    },
    async openDocumentInNewWindow(document) {
      const url = await this.getDocumentPreviewUrl(document);
      if (url) {
        window.open(url, '_blank');
      } else {
        this.showError('Error', 'Failed to open document');
      }
    },
    async uploadDocuments(vendorId) {
      if (!this.documents || this.documents.length === 0) return;
      
      if (!this.companyId) {
        logger.error('Company ID not found');
        return;
      }
      
      // Filter documents that haven't been uploaded yet (have file property)
      const documentsToUpload = this.documents.filter(doc => doc.file);
      
      if (documentsToUpload.length === 0) return;
      
      try {
        const formData = new FormData();
        formData.append('vendorId', vendorId);
        formData.append('companyId', this.companyId);
        
        documentsToUpload.forEach((doc) => {
          if (doc.file) {
            formData.append('documents', doc.file);
          }
        });
        
        const response = await vendorService.uploadVendorDocuments(vendorId, this.companyId, formData);
        logger.log('Documents uploaded successfully', response.data);
        
        // Update documents array with uploaded document IDs
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const uploadedDocs = response.data.data;
          documentsToUpload.forEach((doc, index) => {
            if (uploadedDocs[index]) {
              const uploadedDoc = uploadedDocs[index];
              // Find the document in the array and update it
              const docIndex = this.documents.findIndex(d => d === doc);
              if (docIndex !== -1) {
                this.documents[docIndex].id = uploadedDoc.id;
                this.documents[docIndex].vendorId = uploadedDoc.vendorId;
                this.documents[docIndex].uploadedAt = uploadedDoc.uploadedAt;
                // Keep filePath as data URL for preview, but also store the filePath from server
                this.documents[docIndex].serverFilePath = uploadedDoc.filePath;
                
                // If this is the currently selected document, reload preview
                if (this.selectedDocumentIndex === docIndex) {
                  this.$nextTick(() => {
                    this.selectDocument(docIndex);
                  });
                }
              }
            }
          });
        }
      } catch (error) {
        logger.error('Error uploading documents:', error);
        // Don't throw - allow vendor creation to succeed even if document upload fails
        this.showError('Warning', 'Vendor created but some documents failed to upload: ' + (error.message || 'Please try uploading documents again'));
      }
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    }
  },
  beforeUnmount() {
    // Cleanup blob URLs to prevent memory leaks
    Object.values(this.documentPreviewUrls).forEach(url => {
      if (url && url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url);
      }
    });
  },
  computed: {
    vendorRoleDropdownOptions() {
      return [
        { value: '', label: 'Select' },
        ...VENDOR_ROLES_FOR_SELECT,
      ];
    },
    hasCurrentTabErrors() {
      // Check if current tab has validation errors
      if (this.activeTab === 'general') {
        return (!this.form.vendorId || this.form.vendorId.trim() === '') ||
               this.vendorIdExists ||
               (!this.form.vendorName || this.form.vendorName.trim() === '');
      }
      
      if (this.activeTab === 'details') {
        // Payment Terms validation: must not be empty string, null, undefined, or __add_new__
        // Use == null to check for both null and undefined in one check
        if (this.form.paymentTermId == null || this.form.paymentTermId === '' || this.form.paymentTermId === '__add_new__') {
          return true; // Has errors - button disabled
        }
        
        // Payment Method validation: must not be empty string, null, or undefined
        if (this.form.paymentMethod == null || this.form.paymentMethod === '') {
          return true; // Has errors - button disabled
        }
        
        // No errors - button should be enabled
        return false;
      }
      
      if (this.activeTab === 'contacts') {
        const allContacts = [...this.contacts, ...this.newContacts];
        const hasFilledContact = allContacts.some(contact =>
          (contact.name && String(contact.name).trim()) ||
          (contact.email && String(contact.email).trim()) ||
          (contact.phone && String(contact.phone).trim()) ||
          (contact.title && String(contact.title).trim())
        );
        if (hasFilledContact && !allContacts.some(contact => contact.primary)) {
          return true;
        }
        return false;
      }
      
      if (this.activeTab === 'addresses') {
        return false;
      }
      
      // Documents tab has no validation errors (documents are optional)
      if (this.activeTab === 'documents') {
        return false;
      }
      
      return false;
    },
  },
  watch: {
    'form.openingBalance'(val) {
      // Set balance to match opening balance
      this.form.balance = val;
    },
    contacts: {
      handler(newContacts) {
        // Update vendor's main email and phone from primary contact
        const primary = newContacts.find(c => c.primary);
        if (primary) {
          this.form.email = primary.email || '';
          this.form.phone = primary.phone || '';
        }
      },
      deep: true
    },
    visible(newVal) {
      if (newVal) {
        this.resetForm();
        // Ensure companyId is available before fetching data
        if (this.companyId) {
          this.fetchCompanyAccounts();
          this.fetchPaymentTerms();
          this.fetchShippingMethods();
          this.fetchVendorGroups();
        }
        // Set initial vendor data when dialog becomes visible
        this.$nextTick(() => {
          this.setInitialVendorData();
        });
      }
    },
    companyId(newVal, oldVal) {
      // When companyId changes and dialog is visible, fetch data
      if (newVal && this.visible && newVal !== oldVal) {
        this.fetchCompanyAccounts();
        this.fetchPaymentTerms();
        this.fetchShippingMethods();
        this.fetchVendorGroups();
      }
    },
    initialVendorName(newVal) {
      // Watch for changes to initialVendorName prop when dialog is visible
      if (newVal && newVal.trim() && this.visible) {
        this.$nextTick(() => {
          this.setInitialVendorData();
        });
      }
    },
    initialVendorId(newVal) {
      // Watch for changes to initialVendorId prop when dialog is visible
      if (newVal && newVal.trim() && this.visible && this.initialVendorName) {
        this.$nextTick(() => {
          this.setInitialVendorData();
        });
      }
    }
  },
  created() {
    // If component is created with visible=true and companyId, fetch data
    if (this.visible && this.companyId) {
      this.fetchCompanyAccounts();
      this.fetchPaymentTerms();
      this.fetchShippingMethods();
      this.fetchVendorGroups();
    }
    
    // If component is created with initialVendorName and visible, set the data
    if (this.visible && this.initialVendorName && this.initialVendorName.trim()) {
      this.setInitialVendorData();
    }
  }
};
</script>
